import { Collapse, Tooltip } from "@blueprintjs/core";
import React from "react";

import { getCompletionPercent, isCompleted } from "common/entities/Subscription";
import { ButtonView } from "common/views/button/Button.view";
import { CardView } from "common/views/card/Card.view";
import HeadingView from "common/views/heading/Heading.view";
import Icon from "common/views/icon/Icon";
import IconView from "common/views/icon/Icon.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import ProgressBarView, { ProgressBarVariant } from "common/views/progressBar/ProgressBar.view";
import { RowsView } from "common/views/rows/Rows.view";
import TypographyView from "common/views/typographyView/Typography.view";
import { Fragment } from "core/components/FragmentProvider";
import Label from "core/components/label/Label";
import Badge from "panels/catalog/entities/Badge";
import Course  from "panels/catalog/entities/Course";
import Program from "panels/catalog/entities/Program";
import ProgramTrack from "panels/catalog/entities/ProgramTrack";
import ProgramLabels from "panels/catalog/Program.labels";
import * as catalogActions from "../../catalogActions";

interface IProgramDisplayProps {
	program: Program;
	renderPretest?: (course: Course) => React.ReactNode;
	renderPosttest?: (course: Course) => React.ReactNode;
	renderTrackCourses?: (courses: Course[]) => React.ReactNode;
	renderTracks?: (tracks: ProgramTrack[]) => React.ReactNode;
	renderBadges?: (badges: Badge[]) => React.ReactNode;
	toggleExpand: (key: string) => void;
	expanded: { [key: string]: boolean; };
}

export const title: Fragment<IProgramDisplayProps> = {
	name: "Title",
	component: ({program}) => (
		<HeadingView viewKey="program.title">
			{program && <Label k={`Program.${program.id}.name`} v={program.name} />}
		</HeadingView>
	)
};

export const description: Fragment<IProgramDisplayProps> = {
	name: "Description",
	enabled: ({program}) => !!program.description,
	component: ({program}) => (
		<TypographyView viewKey="program.description" html={program && program.description} />
	)
};

export const progress: Fragment<IProgramDisplayProps> = {
	name: "Progress",
	enabled: ({program}) => program && !!program.totalProgress,
	component: ({program}) => {

		return (
			<ProgressBarView
				viewKey="program.progress"
				variant={ProgressBarVariant.DEFAULT}
				value={program.totalProgress.progress}
				label={
					program.completionType === 'Credit' ? (
						<ProgramLabels.Labels.ProgressCredits
							args={{
								completed: program.totalProgress.completed.toString(),
								total: program.totalProgress.total.toString()
							}}
						/>
					) : (
						<ProgramLabels.Labels.Progress
							args={{
								completed: program.totalProgress.completed.toString(),
								total: program.totalProgress.total.toString()
							}}
						/>
					)
				}
			/>
		);
	}
};

export const badges: Fragment<IProgramDisplayProps> = {
	name: "Badges",
	enabled: ({program, renderBadges}) => program && !!program.badges && program.badges.length > 0 && typeof renderBadges === 'function',
	component: ({program, renderBadges}) => renderBadges(program.badges)
};

export const viewLink: Fragment<IProgramDisplayProps> = {
	name: "View Link",
	component: ({program}) => (
		<PageLinkView params={{programId: program && program.id || 0}} viewKey="program.view">
			<ButtonView viewKey="program.view.button">
				<ProgramLabels.Labels.ProgramView />
			</ButtonView>
		</PageLinkView>
	)
};

export const checklist: Fragment<IProgramDisplayProps> = {
	name: "Checklist",
	component: ({program}) => {

		const steps = [];

		if (program.preTest) {
			steps.push({
				key: 'pretest',
				value: getCompletionPercent(program.preTest.subscription),
				label: program.preTest.displayName || program.preTest.product.name
			});
		}

		if (program.tracks && program.tracks.length > 0) {
			const { total, completed } = (program.tracks || []).reduce((a, c) => {
				if (c.progress) {
					a.completed += c.progress.completed || 0;
					a.total += c.progress.total || 0;
				}
				return a;
			}, {completed: 0, total: 0});

			steps.push({
				keys: 'tracks',
				value: total > 0 ? completed / total : 0,
				label: <ProgramLabels.Labels.Tracks />
			});
		}

		if (program.postTest) {
			steps.push({
				key: 'posttest',
				value: getCompletionPercent(program.postTest.subscription),
				label: program.postTest.displayName || program.postTest.product.name
			});
		}

		return (
			<div>
				{steps.map((step) => {

					return (
						<div key={step.key}>
							<ProgressBarView value={step.value} viewKey="step.progress" />
							<HeadingView viewKey="step.title">
								{step.label}
							</HeadingView>
						</div>
					);
				})}
			</div>
		);
	}
};


export const pretestStrengths: Fragment<IProgramDisplayProps> = {
	name: "Pre Test Strengths",
	enabled: ({program}) => !!program.preTest,
	component: ({program}) => {

		if (!program.preTest.subscription) {

			return (
				<div>
					take the pre test
				</div>
			);
		}

		if (!isCompleted(program.preTest.subscription)) {

			return (
				<div>
					finish it
				</div>
			);
		}

		return (
			<div>
				so many weaknesses
			</div>
		);
	}
};

export const tracks: Fragment<IProgramDisplayProps> = {
	name: "Tracks",
	enabled: ({program, renderTracks}) => !!program.tracks && typeof renderTracks === 'function',
	component: ({program, renderTracks}) => renderTracks(program.tracks)
};

export const afptracks: Fragment<IProgramDisplayProps> = {
	name: "AFP Temporary Tracks Fragment",
	component: ({program, expanded, toggleExpand}) => {

		const sections: Array<{
			key: string;
			progress: number;
			progressLabel?: string | JSX.Element;
			title: JSX.Element | string;
			subtitle: JSX.Element | string;
			status?: JSX.Element | string;
			description?: JSX.Element | string;
			content?: JSX.Element;
			actions?: JSX.Element;
		}> = [];

		const weaknesses: { [id: string]: boolean; } = {};

		if (program.preTest) {

			const { preTest } = program;

			sections.push({
				key: 'pretest',
				progress: getCompletionPercent(preTest.subscription),
				progressLabel: (
					preTest.subscription &&
					preTest.subscription.completion
				),
				title: preTest.displayName || preTest.product.name,
				subtitle: preTest.product.subTitle,
				status: preTest.subscription && preTest.subscription.completedOn && <ProgramLabels.Labels.CompletedOn args={{date: new Date(preTest.subscription.completedOn)}} />,
				actions: preTest.launchURL && preTest.launchURL.launchURL && (
					<ButtonView viewKey="test.launch" onClick={() => window.open(preTest.launchURL.launchURL, '_blank')}>
						<ProgramLabels.Labels.LaunchCourse />
					</ButtonView>
				)
			});

			const weaknessTagIds = preTest.subscription && preTest.subscription.tags && preTest.subscription.tags.reduce((a, c) => {
				if (c.type === 'WEAK') {
					a.push(c.tag.id);
				}
				return a;
			}, []) || [];



			/**
			 * create map of courses that have overlapping tags with the subscription weaknesses
			 */
			if (weaknessTagIds.length > 0 && program.tracks && program.tracks.length) {
				for (const track of program.tracks) {
					if (track.products) {
						for (const p of track.products) {
							if (p.course.tags) {
								for (const t of p.course.tags) {
									if (weaknessTagIds.indexOf(t.id) > -1) {
										weaknesses[p.course.id] = true;
										break;
									}
								}
							}
						}
					}
				}
			}
		}

		const {completed, total} = (program.tracks || []).reduce((a, c) => {
			if (c.progress) {
				a.completed += c.progress.completed || 0;
				a.total += c.progress.total || 0;
			}
			return a;
		}, {completed: 0, total: 0});

		const value = total > 0 ? (completed / total) : 0;

		const progressLabel = <ProgramLabels.Labels.Progress args={{completed: (completed).toString(), total: (total).toString()}} />;

		sections.push({
			key: 'tracks',
			progress: value,
			progressLabel,
			title: "2. Study & Practice",
			subtitle: "Complete the Curriculum",
			status: progressLabel,
			description: (
				Object.keys(weaknesses).length && (
					<IconView viewKey="focus.icon" icon="warning-sign">
						<ProgramLabels.Labels.Focus />
					</IconView>
				)
			),
			content: program.tracks && (
				<div style={{padding: '0 15px 15px'}}>
					<RowsView viewKey="tracks.rows">
						{program.tracks.map((track) => {

							const isExpanded: boolean = !!expanded['track-' + track.id];

							return (
								<div key={track.id}>
									<CardView viewKey="track.card" interactive={true} onClick={() => toggleExpand('track-' + track.id)}>
										<div style={{display: 'flex', justifyContent: 'space-between', padding: '15px'}}>
											<HeadingView viewKey="track.title">
												{track.name}
											</HeadingView>
											<Icon icon={isExpanded ? 'chevron-down' : 'chevron-up'} />
										</div>
									</CardView>
									<Collapse isOpen={isExpanded}>
										{
											track.products && track.products.map((product) => {

												if (!product.course) {
													return null;
												}

												const { course } = product;

												return (
													<div key={course.id} style={{display: 'flex', padding: '15px 0', alignItems: 'center'}}>
														<div style={{flexGrow: 1}}>
															<div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px', alignItems: 'center'}}>
																<HeadingView viewKey="course.title">
																	{course.title}
																</HeadingView>
																{
																	weaknesses[course.id] && (
																		<Tooltip content={<ProgramLabels.Labels.Focus />}>
																			<IconView viewKey="focus.icon" style={{color: 'red'}} icon="warning-sign" />
																		</Tooltip>
																	)
																}
															</div>
															<ProgressBarView
																viewKey="course.progress"
																value={course.progress}
															/>
														</div>
														{
															course.launchURL && (
																<ButtonView
																	viewKey="course.launch"
																	onClick={() => window.open(course.launchURL, '_blank')}
																	style={{marginLeft: '15px'}}
																>
																	<ProgramLabels.Labels.LaunchCourse />
																</ButtonView>
															)
														}
													</div>
												);
											})
										}
									</Collapse>
								</div>
							);
						})}
					</RowsView>
				</div>
			)
		});

		if (program.postTest) {

			const { postTest } = program;

			sections.push({
				key: 'posttest',
				progress: getCompletionPercent(postTest.subscription),
				progressLabel: (
					postTest.subscription &&
					postTest.subscription.completion
				),
				title: postTest.displayName || postTest.product.name,
				subtitle: postTest.product.subTitle,
				status: postTest.subscription && postTest.subscription.completedOn && <ProgramLabels.Labels.CompletedOn args={{date: new Date(postTest.subscription.completedOn)}} />,
				actions: postTest.launchURL && postTest.launchURL.launchURL && (
					<ButtonView viewKey="test.launch" onClick={() => window.open(postTest.launchURL.launchURL, '_blank')}>
						<ProgramLabels.Labels.LaunchCourse />
					</ButtonView>
				)
			});
		}

		return (
			<React.Fragment>
				{sections.map((section) => {

					return (
						<CardView key={section.key} viewKey="section">
							<div style={{display: 'flex'}}>
								<div style={{minWidth: '60px', padding: '15px 0 15px 15px'}}>
									<ProgressBarView
										viewKey="section.progress"
										value={section.progress}
										label={section.progressLabel}
									/>
								</div>
								<div style={{flexGrow: 1, padding: '15px'}}>
									<HeadingView viewKey="section.subtitle">
										{section.subtitle}
									</HeadingView>
									<HeadingView viewKey="section.title">
										{section.title}
									</HeadingView>
									{
										section.status && (
											<HeadingView viewKey="section.status">
												{section.status}
											</HeadingView>
										)
									}
									{section.description || null}
								</div>
								{
									!!section.actions && (
										<div style={{padding: '15px 15px 15px 0'}}>
											{section.actions}
										</div>
									)
								}
							</div>
							{section.content}
						</CardView>
					);
				})}
			</React.Fragment>
		);
	}
};



// WEBPACK FOOTER //
// ./src/panels/catalog/fragments/program/displayFragments.tsx